/**
 * Business-number field module — modular-form replacement for the legacy
 * business-number input + ABN lookup.
 *
 * Replaces:
 *  • Schema → packages/frontend/src/schema/workspaceSchema.ts:22-54
 *      businessNumber: yup.string().when(['businessNumberCountry','canProceed'], ...)
 *        .test('business-number-validator', ...)  // format check, AU+canProceed short-circuit
 *        .when(['entityType'], ...)               // nullable for Individual, else required
 *  • UI + lookup wiring → BusinessDetailsForm.tsx:375-413 (<BusinessNumberInputFieldV2 .../>)
 *    and the parent callbacks WorkspaceDetailsForm.tsx:420-433:
 *      onBusinessDetailsChange={(isVerified, entityName='') => { setValue('businessNumberVerified', isVerified); setValue('entityName', entityName); }}
 *      onProceedChange={(canProceed) => { trigger('businessNumber'); setValue('canProceed', canProceed); }}
 *  • The async ABN lookup itself → BusinessNumberInputFieldV2.tsx:54-75 (now ./useBusinessNumberLookup.ts).
 *
 * Behaviour change: always editable; `disabled` only for a verified AU business (FR-013).
 */
import { EntityTypeEnum } from '@sr-sdks/api-sdk-axios';
import {
  Button,
  Dialog,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icon,
  Input,
} from '@sr/ui-library';
import { ChangeEvent, KeyboardEvent } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { businessNumberFormatter } from '../../../../utils/helpers/businessNumberFormatter';
import {
  getBusinessNumberLabel,
  shouldVerifyBusinessNumber,
} from '../../../../utils/helpers/common';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsFormValues } from '../../shared/types';
import { useBusinessNumberLookup } from './useBusinessNumberLookup';

interface BusinessNumberFieldProps {
  businessNumberLabel: string;
  /** R1: disabled when editing a verified AU business (see plan.md). */
  disabled?: boolean;
  /** Needs the form instance for the imperative ABN lookup + AU on-change resets. */
  form: UseFormReturn<WorkspaceDetailsFormValues>;
}

export const BusinessNumberField = ({
  businessNumberLabel,
  disabled,
  form,
}: BusinessNumberFieldProps) => {
  const { control, setValue } = form;
  const {
    isLoading,
    isVerifyDialogOpen,
    lookup,
    proceedUnverified,
    setIsVerifyDialogOpen,
  } = useBusinessNumberLookup({ form });

  // FR-015: verified checkmark — distinct from ABN Status badge.
  const businessNumberVerified = useWatch({
    control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.businessNumberVerified,
  });
  const businessNumberCountry = useWatch({
    control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.businessNumberCountry,
  });

  const verifiable = shouldVerifyBusinessNumber(businessNumberCountry);

  return (
    <>
      <FormField
        name={WORKSPACE_DETAILS_FORM_FIELDS.businessNumber}
        control={control}
        render={({ field }) => {
          /**
           * Enabled on a well-formed number, read straight from the formatter
           * rather than from `fieldState.invalid`: the field is *expected* to
           * be invalid at this point — an un-looked-up AU number carries the
           * "Please look up your ABN" error — and gating the button on that
           * would deadlock the only control that clears it.
           */
          const isLookupEnabled =
            !disabled &&
            Boolean(field.value?.trim()) &&
            businessNumberFormatter(businessNumberCountry || '', field.value)
              .isValid;

          return (
            <FormItem>
              <FormLabel
                asterisk
                tooltipContent={`If you want to update your ${businessNumberLabel}, email support@screenrights.org.`}
              >
                {businessNumberLabel}
              </FormLabel>
              <FormControl>
                <div className="flex items-start gap-2">
                  <Input
                    {...field}
                    disabled={disabled}
                    value={field.value ?? ''}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const { value } = businessNumberFormatter(
                        businessNumberCountry || '',
                        event.target.value,
                      );

                      field.onChange(value);
                      // AU: each keystroke invalidates the verified entity name
                      // and re-gates submit until a fresh lookup succeeds.
                      if (verifiable) {
                        setValue(WORKSPACE_DETAILS_FORM_FIELDS.entityName, '', {
                          shouldDirty: true,
                        });
                        setValue(
                          WORKSPACE_DETAILS_FORM_FIELDS.canProceed,
                          false,
                        );
                      }
                    }}
                    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                      if (event.key === 'Enter' && isLookupEnabled) {
                        event.preventDefault();
                        void lookup();
                      }
                    }}
                  />

                  {verifiable && (
                    <Button
                      className="shrink-0"
                      disabled={!isLookupEnabled}
                      icon={<Icon icon="search" />}
                      loading={isLoading}
                      size="icon"
                      type="button"
                      variant="outline"
                      onClick={() => void lookup()}
                    />
                  )}
                </div>
              </FormControl>

              <div className="flex justify-between gap-2">
                <FormMessage />

                {verifiable && (
                  <p className="whitespace-nowrap text-sm [fieldset:disabled_&]:opacity-50">
                    Lookup Status:{' '}
                    {businessNumberVerified ? (
                      <span className="text-ui-success">Valid</span>
                    ) : (
                      <span className="text-ui-danger">Invalid</span>
                    )}
                  </p>
                )}
              </div>
            </FormItem>
          );
        }}
      />

      <Dialog
        heading={`${businessNumberLabel} couldn't be verified`}
        actionButtonProps={{
          children: 'Proceed',
          onClick: proceedUnverified,
        }}
        cancelButtonProps={{ children: 'Cancel' }}
        open={isVerifyDialogOpen}
        onOpenChange={setIsVerifyDialogOpen}
      >
        We couldn&apos;t verify the {businessNumberLabel}. You can still proceed
        to save the unverified {businessNumberLabel}, but it may delay your
        payments.
      </Dialog>
    </>
  );
};

BusinessNumberField.schema = yup
  .string()
  .when(
    ['businessNumberCountry', 'canProceed', 'entityType'],
    ([businessNumberCountry, canProceed, entityType], schema) => {
      /**
       * Individuals have no business-number field rendered, but react-hook-form
       * keeps whatever was typed before the switch. Skipping every check — not
       * just the `required` — stops an invisible "not valid" error from
       * blocking submit; the submit mapper blanks the value anyway.
       */
      if (entityType === EntityTypeEnum.Individual) return schema.nullable();

      /**
       * Not rendered until a country and an entity type are chosen (legacy
       * gate, mirrored in WorkspaceDetailsEntitySection), so it must not block
       * submit from behind — the country and entity-type fields carry their
       * own visible required errors.
       */
      if (!businessNumberCountry || !entityType) return schema.nullable();

      /**
       * The label follows the selected country — CRN, NZBN, UID, or the generic
       * "Business Number" — so the messages have to as well. v2 did this by
       * substituting a `[label]` token at render time; naming the country's
       * label here keeps a non-AU user from being told their CRN is an invalid
       * ABN (APP-2237, QA note 9 / APP-2114).
       */
      const businessNumberLabel = getBusinessNumberLabel(businessNumberCountry);

      return schema
        .test('business-number-validator', (value, { createError, path }) => {
          // AU + verified/proceeded short-circuits format validation.
          if (canProceed && businessNumberCountry === 'AU') return true;
          // An empty value is `.required`'s job, not this test's.
          if (value == null || value === '') return true;

          const { isValid } = businessNumberFormatter(
            businessNumberCountry || '',
            value || '',
          );

          if (!isValid) {
            return createError({
              message: `${businessNumberLabel} is not valid`,
              path,
            });
          }

          /**
           * AU only: a well-formed ABN still has to go through the lookup, and
           * the gate above is the only thing that opens it. Legacy leaned on
           * entity-name's `required` rule to block this, which surfaced the
           * error on a read-only field the user could not fill (APP-2237 QA).
           * Say what to do, on the field that has the Lookup button.
           */
          if (businessNumberCountry === 'AU') {
            return createError({
              message: `Please look up your ${businessNumberLabel} to verify it`,
              path,
            });
          }

          return true;
        })
        .required(`Please enter ${businessNumberLabel}`);
    },
  );
